/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  RankedTester,
  rankWith,
  UISchemaElement,
  uiTypeIs,
  RendererProps,
} from "@jsonforms/core"; 
import { Divider, Button, StatusLight } from "@adobe/react-spectrum";
import {
  JsonFormsDispatch,
  useJsonForms,
  withJsonFormsLayoutProps,
} from "@jsonforms/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { get } from "lodash";

// Utils
import { evaluateContextRule, extractPath, resolveContextValue } from "../utils/commonUtils";

import styles from "./css/ActionCardRenderer.module.scss";

type actionsObj = {
  label: string;
  path: string;
  inSameApp?: boolean;
  inSameTab?: boolean;
};

type optionsType = {
  statusColor?: string;
  statusLabel?: string;
  contents?: any;
  actions?: actionsObj[];
  style?: any;
  statusLabelStyle?: any;
  statusLightString?: string;
  statusVariant?: string;
};

export const actionCardRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("ActionCard")
);
function ActionCardRenderer({ uischema, visible, enabled }: RendererProps) {
  const optionsSchema = uischema as UISchemaElement;
  const {
    contents,
    style,
    statusLabelStyle,
    statusLightString = "",
    statusVariant = "",
  } = optionsSchema.options as optionsType;
  const actions = optionsSchema?.options?.actions || [];
  const redirectPath = optionsSchema.options?.redirect?.path;
  const contentStyle = contents?.options?.style;

  let statusLabel = uischema.options?.statusLabel;
  const reduxStateObj = useSelector((state: any) => state?.reduxState);
  const contextStatusLabel = resolveContextValue(reduxStateObj, statusLabel);
  if (typeof contextStatusLabel !== "undefined") {
    statusLabel = contextStatusLabel;
  }

  let statusColor = uischema.options?.statusColor;
  const contextStatusColor = resolveContextValue(reduxStateObj, statusColor);
  if (typeof contextStatusColor !== "undefined") {
    statusColor = contextStatusColor;
  }

  let contentHeight = "auto";
  if (style?.height) {
    contentHeight = style.height;
    delete style.height;
  }

  const navigate = useNavigate();
  const { renderers, cells, config: jsonFormConfig } = useJsonForms();

  const externalHandlers = jsonFormConfig?.lifecycleHooks?.externalHandlers;

  const externalVariantHandler = externalHandlers?.getVariantFromExternal;

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    const reduxStateData = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation = evaluateContextRule(
      uischema.options?.contextRule,
      reduxStateData
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  const getButtons = () => {
    const buttons: JSX.Element[] = [];
    actions.forEach((obj: actionsObj) => {
      if (obj.inSameApp) {
        buttons.push(
          <Button
            key={obj.label}
            variant="cta"
            data-testid="actionButton"
            isDisabled={!componentEnabled}
            onPress={() => {
              navigate(obj.path);
            }}
          >
            {obj.label}
          </Button>
        );
      } else if (obj.inSameTab) {
        buttons.push(
          <Button
            key={obj.label}
            variant="cta"
            href={obj.path}
            elementType="a"
            data-testid="actionButton"
            isDisabled={!componentEnabled}
          >
            {obj.label}
          </Button>
        );
      } else {
        buttons.push(
          <Button
            key={obj.label}
            variant="cta"
            target="_blank"
            rel="noreferrer"
            href={obj.path}
            elementType="a"
            data-testid="actionButton"
            isDisabled={!componentEnabled}
          >
            {obj.label}
          </Button>
        );
      }
    });
    return buttons;
  };

  const getStatusVariant = () => {
    const variants = {
      active: "positive",
      inactive: "negative",
      draft: "neutral",
    };
    return variants[statusLightString.toLowerCase()];
  };

  const getStatusRenderer = () => (
    <div className={styles.statusContainer} data-testid="card_status_light">
      <Divider size="M" data-testid="divider" />
      <div>
        <StatusLight
          aria-label="status-light"
          role="status"
          variant={
            (externalVariantHandler &&
              externalVariantHandler(
                statusLightString.toLowerCase().replace(/ /g, "_")
              )) ||
            statusVariant ||
            getStatusVariant()
          }
          UNSAFE_className={styles.cardStatus}
        >
          {`${statusLightString[0].toUpperCase()}${statusLightString
            .slice(1)
            .toLowerCase()}`}
        </StatusLight>
      </div>
    </div>
  );

  return (
    <div
      className={
        redirectPath
          ? `${styles.container} ${styles.pointerCls}`
          : `${styles.container}`
      }
      style={style}
      onClick={() => redirectPath && navigate(redirectPath)}
      onKeyDown={() => redirectPath && navigate(redirectPath)}
      role="button"
      tabIndex={0}
      hidden={!componentVisibility}
    >
      {statusLabel && (
        <div
          className={styles.statusBadge}
          style={{ backgroundColor: statusColor, ...statusLabelStyle }}
          data-testid="statusBadge"
        >
          {statusLabel}
        </div>
      )}
      <div>
        <div
          className={styles.content}
          data-testid="content"
          style={{ height: contentHeight, ...contentStyle }}
        >
          <JsonFormsDispatch
            renderers={renderers}
            cells={cells}
            uischema={contents?.uischema}
            schema={{}}
          />
        </div>
        {actions.length > 0 && (
          <div className={styles.actionsContainer} data-testid="actions">
            <Divider size="M" data-testid="divider" />
            <div className={styles.buttonsContainer}>{getButtons()}</div>
          </div>
        )}
        {statusLightString && getStatusRenderer()}
      </div>
    </div>
  );
}
export default withJsonFormsLayoutProps(ActionCardRenderer);
